/**
 * Smart aggregation for recipe ingredients → grocery list.
 * - Normalize ingredient names for matching
 * - Find matching existing items
 * - Check unit compatibility
 * - Aggregate quantities and track source recipes
 */

/** Parsed representation of an ingredient line (e.g. "2 cups flour") */
export interface ParsedIngredient {
  /** Original display string */
  original: string;
  /** Numeric quantity if present */
  quantity: number;
  /** Unit (e.g. "cups", "tbsp", "whole"); "unit" if unspecified */
  unit: string;
  /** Ingredient name (e.g. "flour", "yellow onion") */
  name: string;
  /** Normalized name for matching */
  normalizedName: string;
}

/** Grocery item shape used for aggregation (minimal fields needed) */
export interface GroceryItemForMatch {
  id: string;
  name: string;
  normalizedName?: string;
  quantity?: number;
  unit?: string;
  checked?: boolean;
  sourceRecipes?: string[];
  [key: string]: unknown;
}

/** Result of aggregating new ingredients into an existing list */
export interface AggregationResult {
  /** Merged items to set on the list (existing + new/merged) */
  items: GroceryItemForMatch[];
  /** Count of newly added lines (before merging) */
  addedCount: number;
  /** Count of items that were merged into existing */
  mergedCount: number;
}

const UNIT_ALIASES: Record<string, string> = {
  cup: 'cups',
  cups: 'cups',
  c: 'cups',
  tablespoon: 'tbsp',
  tablespoons: 'tbsp',
  tbsp: 'tbsp',
  tbs: 'tbsp',
  tb: 'tbsp',
  teaspoon: 'tsp',
  teaspoons: 'tsp',
  tsp: 'tsp',
  ts: 'tsp',
  gram: 'g',
  grams: 'g',
  g: 'g',
  kilogram: 'kg',
  kilograms: 'kg',
  kg: 'kg',
  ounce: 'oz',
  ounces: 'oz',
  oz: 'oz',
  lb: 'lb',
  lbs: 'lb',
  pound: 'lb',
  pounds: 'lb',
  whole: 'whole',
  piece: 'whole',
  pieces: 'whole',
  clove: 'clove',
  cloves: 'clove',
  pinch: 'pinch',
  can: 'can',
  cans: 'can',
  bunch: 'bunch',
  bunches: 'bunch',
  slice: 'slice',
  slices: 'slice',
  sprig: 'sprig',
  sprigs: 'sprig',
  stalk: 'stalk',
  stalks: 'stalk',
  head: 'head',
  heads: 'head',
  package: 'package',
  packages: 'package',
  bag: 'bag',
  bags: 'bag',
  bottle: 'bottle',
  bottles: 'bottle',
};

const UNIT_REGEX = new RegExp(
  `^(${Object.keys(UNIT_ALIASES).join('|')})(?:s)?\\b\\s*`,
  'i'
);

/** Normalize unit string for comparison (lowercase, alias to canonical) */
export function normalizeUnit(unit: string): string {
  const u = unit.toLowerCase().trim();
  if (!u) return 'unit';
  return UNIT_ALIASES[u] ?? u;
}

const SIZE_OR_FRESH_PREFIX =
  /^(fresh|large|small|medium|ripe|boneless|skinless)\s+/i;

const TRAILING_PREP =
  /,?\s*(diced|minced|chopped|sliced|grated|optional|to taste|divided|for garnish|thinly sliced|roughly chopped|room temperature|plus more for serving)\s*$/gi;

/** First letter capitalized; rest of the string unchanged (grocery list display). */
export function groceryDisplayName(name: string): string {
  const t = name.replace(/\s+/g, ' ').trim();
  if (!t) return t;
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function singularizeLastWord(phrase: string): string {
  const words = phrase.split(' ');
  const last = words[words.length - 1];
  if (!last) return phrase;
  words[words.length - 1] = singularizeWord(last);
  return words.join(' ');
}

function singularizeWord(s: string): string {
  if (s.length <= 2) return s;
  if (['rice', 'cheese', 'sauce', 'molasses', 'asparagus'].includes(s)) return s;
  if (s.endsWith('ies') && s.length > 4) return s.slice(0, -3) + 'y';
  if (s.endsWith('oes') && s.length > 4) return s.slice(0, -2);
  if (s.endsWith('s') && !s.endsWith('ss')) return s.slice(0, -1);
  return s;
}

function stripSizeOrFreshPrefixes(s: string): string {
  let next = s;
  for (;;) {
    const stripped = next.replace(SIZE_OR_FRESH_PREFIX, '').trim();
    if (stripped === next) return next;
    next = stripped;
  }
}

/**
 * Normalize ingredient name for matching: lowercase, trim, remove common
 * descriptors and plurals so "yellow onions" and "onion" can match.
 */
export function normalizeIngredientName(name: string): string {
  let s = name.toLowerCase().trim();
  if (!s) return '';

  s = s.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
  s = stripSizeOrFreshPrefixes(s);
  s = s.replace(TRAILING_PREP, '').trim();
  s = s.replace(/\s+/g, ' ').trim();
  s = singularizeLastWord(s);
  return s;
}

const UNICODE_FRACTIONS: Record<string, number> = {
  '½': 0.5,
  '⅓': 1 / 3,
  '⅔': 2 / 3,
  '¼': 0.25,
  '¾': 0.75,
  '⅕': 0.2,
  '⅖': 0.4,
  '⅗': 0.6,
  '⅘': 0.8,
  '⅙': 1 / 6,
  '⅛': 0.125,
  '⅜': 0.375,
  '⅝': 0.625,
  '⅞': 0.875,
};

function parseFractionToken(token: string): number | null {
  const uni = UNICODE_FRACTIONS[token];
  if (uni != null) return uni;
  if (token.includes('/')) {
    const [a, b] = token.split('/').map((p) => parseFloat(p.trim()));
    if (Number.isFinite(a) && Number.isFinite(b) && b !== 0) return a / b;
  }
  const n = parseFloat(token);
  return Number.isFinite(n) ? n : null;
}

/** Leading qty: "2", "1/2", "1½", "1 1/2", "2-3", "3–4". Ranges use the higher amount. */
function splitLeadingQuantity(original: string): { quantity: number; rest: string } {
  const s = original.trim();
  const range = s.match(/^(\d+(?:\.\d+)?)\s*[-–—]\s*(\d+(?:\.\d+)?)\b\s*/);
  if (range) {
    const hi = Math.max(parseFloat(range[1]), parseFloat(range[2]));
    return { quantity: hi, rest: s.slice(range[0].length).trim() };
  }
  const mixedUni = s.match(/^(\d+)\s*([½⅓⅔¼¾⅕⅖⅗⅘⅙⅛⅜⅝⅞])(?=\s|$)/);
  if (mixedUni) {
    const q = parseFloat(mixedUni[1]) + (UNICODE_FRACTIONS[mixedUni[2]] ?? 0);
    return { quantity: q, rest: s.slice(mixedUni[0].length).trim() };
  }
  const mixedAscii = s.match(/^(\d+)\s+(\d+\s*\/\s*\d+)\b\s*/);
  if (mixedAscii) {
    const q = parseFloat(mixedAscii[1]) + (parseFractionToken(mixedAscii[2].replace(/\s+/g, '')) ?? 0);
    return { quantity: q, rest: s.slice(mixedAscii[0].length).trim() };
  }
  const uni = s.match(/^([½⅓⅔¼¾⅕⅖⅗⅘⅙⅛⅜⅝⅞])(?:\s+|$)/);
  if (uni) {
    return { quantity: UNICODE_FRACTIONS[uni[1]] ?? 1, rest: s.slice(uni[0].length).trim() };
  }
  const numberMatch = s.match(/^(\d+(?:\.\d+)?(?:\s*\/\s*\d+)?)\s*/);
  if (numberMatch) {
    const quantity = parseFractionToken(numberMatch[1].replace(/\s+/g, '')) ?? 1;
    return { quantity, rest: s.slice(numberMatch[0].length).trim() };
  }
  return { quantity: 1, rest: s };
}

function cleanNamePart(namePart: string): string {
  let s = namePart.trim();
  s = s.replace(/^of\s+/i, '').trim();
  s = stripSizeOrFreshPrefixes(s);
  s = s.replace(TRAILING_PREP, '').trim();
  s = s.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
  return s;
}

/**
 * Parse an ingredient string into quantity, unit, and name.
 * Handles: "2 cups flour", "1 yellow onion", "salt to taste", "3–4 cloves garlic"
 */
export function parseIngredient(ingredientStr: string): ParsedIngredient {
  const original = ingredientStr.trim();
  const normalizedName = normalizeIngredientName(original);

  const { quantity, rest } = splitLeadingQuantity(original);

  let unit = 'unit';
  let namePart = rest;
  const unitMatch = rest.match(UNIT_REGEX);
  if (unitMatch) {
    unit = normalizeUnit(unitMatch[1]);
    namePart = rest.slice(unitMatch[0].length).trim();
  } else {
    // Check for "whole" or "piece" later in string, or default by quantity
    if (quantity === 1 && !/\d/.test(namePart)) unit = 'whole';
    if (namePart.toLowerCase().startsWith('whole ')) {
      unit = 'whole';
      namePart = namePart.slice(6).trim();
    }
  }

  const cleaned = cleanNamePart(namePart);
  const name = cleaned || original;
  const nameNormalized = cleaned ? normalizeIngredientName(cleaned) : normalizedName;

  return {
    original,
    quantity,
    unit,
    name: groceryDisplayName(name),
    normalizedName: nameNormalized || normalizedName,
  };
}

/**
 * Check if two units are compatible for aggregating quantities.
 * Same unit → yes; both "unit" or "whole" → yes; otherwise no (no conversion).
 */
export function areUnitsCompatible(unitA: string, unitB: string): boolean {
  const a = normalizeUnit(unitA || 'unit');
  const b = normalizeUnit(unitB || 'unit');
  if (a === b) return true;
  // Treat "unit" and "whole" as compatible with each other
  if ((a === 'unit' || a === 'whole') && (b === 'unit' || b === 'whole')) return true;
  return false;
}

/**
 * Find an existing list item that matches this parsed ingredient (same normalized name).
 */
export function findMatchingIngredient(
  parsed: ParsedIngredient,
  existingItems: GroceryItemForMatch[]
): GroceryItemForMatch | undefined {
  const targetNorm = parsed.normalizedName;
  if (!targetNorm) return undefined;
  return existingItems.find((item) => {
    const itemNorm = (item.normalizedName ?? normalizeIngredientName(item.name)).toLowerCase();
    return itemNorm === targetNorm;
  });
}

/**
 * Aggregate new recipe ingredients into an existing list:
 * - Match by normalized name
 * - If match and units compatible → add quantity and add recipe to sourceRecipes
 * - Otherwise append as new item
 * New items get id, name, normalizedName, quantity, unit, category, checked, sourceRecipes, etc.
 */
export function aggregateIngredients(
  existingItems: GroceryItemForMatch[],
  newIngredientStrings: string[],
  recipeId: string,
  options: {
    generateId?: () => string;
    now?: number;
    categoryForItem?: (name: string) => string;
  } = {}
): AggregationResult {
  const generateId = options.generateId ?? (() => `item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);
  const now = options.now ?? Date.now();
  const categoryForItem = options.categoryForItem ?? (() => 'Other');

  const resultItems = existingItems.map((item) => ({ ...item }));
  let mergedCount = 0;
  let addedCount = 0;

  for (const line of newIngredientStrings) {
    const parsed = parseIngredient(line);
    const match = findMatchingIngredient(parsed, resultItems);

    // Only merge into an existing item if it's unchecked (still to buy). Checked items
    // are "already bought", so additional quantity from a recipe becomes a new line.
    const canMerge =
      match &&
      areUnitsCompatible(match.unit ?? 'unit', parsed.unit) &&
      match.checked !== true;

    if (canMerge) {
      const existingQ = typeof match.quantity === 'number' ? match.quantity : 0;
      const newQ = parsed.quantity;
      const updated = resultItems.find((i) => i.id === match.id);
      if (updated) {
        updated.quantity = existingQ + newQ;
        updated.sourceRecipes = [...new Set([...(updated.sourceRecipes ?? []), recipeId])];
        mergedCount += 1;
      }
    } else {
      resultItems.push({
        id: generateId(),
        name: parsed.name || parsed.original,
        normalizedName: parsed.normalizedName,
        quantity: parsed.quantity,
        unit: parsed.unit === 'unit' ? undefined : parsed.unit,
        category: categoryForItem(parsed.name || parsed.original),
        checked: false,
        recipeId,
        sourceRecipes: [recipeId],
        addedAt: now,
      } as GroceryItemForMatch);
      addedCount += 1;
    }
  }

  return {
    items: resultItems,
    addedCount,
    mergedCount,
  };
}
